/**
 * apps/automations-portal/src/features/admin/ConfigForm.tsx
 *
 * Schema-driven config renderer — the heart of the admin deploy UX. Given a
 * block's ordered `fields` array, renders the right control per field type,
 * validates client-side, and reports a plain config object plus overall
 * validity back to the caller (DeployBlockWizard).
 *
 * Deliberately a controlled component: the wizard owns `value` across its
 * steps (needed for the review step), ConfigForm only renders + validates.
 */
import { useEffect, useId, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { FieldDef } from '@/lib/types';

export type ConfigValue = Record<string, unknown>;

export interface ConfigFormProps {
  fields: FieldDef[];
  value: ConfigValue;
  onChange: (value: ConfigValue) => void;
  onValidChange?: (valid: boolean) => void;
  /**
   * Per-field problems returned by the server on a rejected deploy. Shown in
   * place on the offending field instead of as one opaque message.
   */
  serverIssues?: { key: string; message: string }[] | undefined;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function defaultFor(field: FieldDef): unknown {
  if (field.default !== undefined) return field.default;
  switch (field.type) {
    case 'boolean':
      return false;
    case 'textarea[]':
      return [''];
    case 'number':
      return '';
    default:
      return '';
  }
}

/** Returns an error message for a single field's current value, or undefined when valid. */
export function validateField(field: FieldDef, raw: unknown): string | undefined {
  switch (field.type) {
    case 'boolean':
      return undefined;

    case 'textarea[]': {
      const items = Array.isArray(raw) ? (raw as string[]) : [];
      const nonEmpty = items.filter((s) => s.trim().length > 0);
      const min = field.min ?? (field.required ? 1 : 0);
      if (nonEmpty.length < min) {
        return `Add at least ${min} ${min === 1 ? 'entry' : 'entries'}.`;
      }
      if (field.max !== undefined && items.length > field.max) {
        return `No more than ${field.max} ${field.max === 1 ? 'entry' : 'entries'} allowed.`;
      }
      return undefined;
    }

    case 'number': {
      const str = String(raw ?? '').trim();
      if (str.length === 0) {
        return field.required ? `${field.label} is required.` : undefined;
      }
      const num = Number(str);
      if (Number.isNaN(num)) return `${field.label} must be a number.`;
      if (field.min !== undefined && num < field.min) return `${field.label} must be at least ${field.min}.`;
      if (field.max !== undefined && num > field.max) return `${field.label} must be at most ${field.max}.`;
      return undefined;
    }

    case 'email': {
      const str = String(raw ?? '').trim();
      if (str.length === 0) return field.required ? `${field.label} is required.` : undefined;
      return EMAIL_RE.test(str) ? undefined : `Enter a valid email address.`;
    }

    case 'url': {
      const str = String(raw ?? '').trim();
      if (str.length === 0) return field.required ? `${field.label} is required.` : undefined;
      try {
        new URL(str);
        return undefined;
      } catch {
        return `Enter a valid URL, including https://.`;
      }
    }

    case 'text':
    case 'textarea':
    default: {
      const str = String(raw ?? '').trim();
      if (field.required && str.length === 0) return `${field.label} is required.`;
      return undefined;
    }
  }
}

/** Coerces a form's raw values into the plain config object handed to the backend. */
export function toConfigObject(fields: FieldDef[], value: ConfigValue): ConfigValue {
  const out: ConfigValue = {};
  for (const field of fields) {
    const raw = value[field.key];
    switch (field.type) {
      case 'boolean':
        out[field.key] = Boolean(raw);
        break;
      case 'number': {
        const str = String(raw ?? '').trim();
        out[field.key] = str.length === 0 ? undefined : Number(str);
        break;
      }
      case 'textarea[]': {
        const items = Array.isArray(raw) ? (raw as string[]) : [];
        out[field.key] = items.map((s) => s.trim()).filter((s) => s.length > 0);
        break;
      }
      default:
        out[field.key] = String(raw ?? '').trim();
    }
  }
  return out;
}

export function ConfigForm({
  fields,
  value,
  onChange,
  onValidChange,
  serverIssues,
}: ConfigFormProps) {
  // Never assume the server sent a list. A block with no configuration schema
  // should render an empty form, not crash the deploy wizard.
  const safeFields = useMemo(() => (Array.isArray(fields) ? fields : []), [fields]);

  const errors = useMemo(() => {
    const map: Record<string, string> = {};
    for (const field of safeFields) {
      const raw = value[field.key] ?? defaultFor(field);
      const err = validateField(field, raw);
      if (err) map[field.key] = err;
    }
    // Server issues win. The backend re-validates on deploy against the
    // authoritative field definitions, so if it rejected a value, showing our
    // own "looks fine" verdict next to it would just be confusing.
    for (const issue of serverIssues ?? []) {
      map[issue.key] = issue.message;
    }
    return map;
  }, [safeFields, value, serverIssues]);

  const isValid = Object.keys(errors).length === 0;

  useEffect(() => {
    onValidChange?.(isValid);
  }, [isValid, onValidChange]);

  function setField(key: string, next: unknown) {
    onChange({ ...value, [key]: next });
  }

  return (
    <div className="flex flex-col gap-5">
      {safeFields.map((field) => (
        <ConfigField
          key={field.key}
          field={field}
          value={value[field.key] ?? defaultFor(field)}
          error={errors[field.key]}
          onChange={(next) => setField(field.key, next)}
        />
      ))}
    </div>
  );
}

function ConfigField({
  field,
  value,
  error,
  onChange,
}: {
  field: FieldDef;
  value: unknown;
  // `| undefined` is required, not redundant: exactOptionalPropertyTypes
  // distinguishes "absent" from "present and undefined", and a lookup into
  // the errors map yields the latter.
  error?: string | undefined;
  onChange: (next: unknown) => void;
}) {
  const id = useId();

  if (field.type === 'boolean') {
    return (
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={Boolean(value)}
          onCheckedChange={onChange}
          aria-describedby={field.help ? `${id}-help` : undefined}
        />
        <div className="flex flex-col gap-1">
          <Label htmlFor={id}>
            {field.label}
            {field.required && <span className="text-destructive"> *</span>}
          </Label>
          {field.help && (
            <p id={`${id}-help`} className="text-xs text-muted-foreground">
              {field.help}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (field.type === 'textarea[]') {
    const items = Array.isArray(value) ? (value as string[]) : [''];
    const max = field.max;
    const min = field.min ?? (field.required ? 1 : 0);
    const atMax = max !== undefined && items.length >= max;
    const atMin = items.length <= Math.max(min, 1);

    return (
      <div className="flex flex-col gap-1.5">
        <Label>
          {field.label}
          {field.required && <span className="text-destructive"> *</span>}
        </Label>
        {field.help && <p className="text-xs text-muted-foreground">{field.help}</p>}
        <div className="flex flex-col gap-2">
          {items.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="flex gap-2">
              <Textarea
                value={item}
                aria-label={`${field.label} ${index + 1}`}
                onChange={(e) => {
                  const next = [...items];
                  next[index] = e.target.value;
                  onChange(next);
                }}
                rows={2}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={atMin}
                aria-label={`Remove ${field.label} ${index + 1}`}
                onClick={() => onChange(items.filter((_, i) => i !== index))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={atMax}
          className="w-fit"
          onClick={() => onChange([...items, ''])}
        >
          <Plus className="h-4 w-4" />
          Add another
        </Button>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {field.label}
        {field.required && <span className="text-destructive"> *</span>}
      </Label>
      {field.type === 'textarea' ? (
        <Textarea
          id={id}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          className={cn(error && 'border-destructive')}
        />
      ) : (
        <Input
          id={id}
          type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
          value={String(value ?? '')}
          min={field.type === 'number' ? field.min : undefined}
          max={field.type === 'number' ? field.max : undefined}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          className={cn(error && 'border-destructive')}
        />
      )}
      {field.help && !error && <p className="text-xs text-muted-foreground">{field.help}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
